<template>
  <p class="mt-2 error-text" v-if="errorMessage">{{errorMessage}}</p>
  <div v-else class="map">
    <l-map ref="heatMap" :zoom="dynamicZoom" :center="dynamicCenter" >
        <l-geo-json v-if="!zoneToBeHighlighted" :geojson="usStatesData" :options="borderOptions"></l-geo-json>
        <l-geo-json v-if="!zoneToBeHighlighted" :geojson="canadaProvincesData" :options="borderOptions"></l-geo-json>
        <l-tile-layer
          v-if="zoneToBeHighlighted"
            v-for="tileProvider in tileProviders"
            :key="tileProvider.name"
            :name="tileProvider.name"
            :visible="tileProvider.visible"
            :url="tileProvider.url"
            :attribution="tileProvider.attribution"
            layer-type="base"
          />
        <l-geo-json v-if="!isLoading" :geojson="coordinates" :options="mapOptions" ref="geolayer"></l-geo-json>
    </l-map>
  </div>
</template>
  
  <script>
  import L from 'leaflet'
  import 'leaflet/dist/leaflet.css'
  import DOMPurify from 'dompurify'
  import { bbox, featureCollection } from '@turf/turf'
  
  import {
    LMap,
    LTileLayer,
    LControlScale,
    LControlLayers,
    LControlZoom,
    LGeoJson
  } from 'vue2-leaflet'
  
  export default {
    name: 'HeatMap',
    data () {
      return {
        labelLayers: null,
        isLoading: true,
        usStatesData: null,
        canadaProvincesData: null,
        borderOptions: {
          style: this.styleBorders
        },
        mapOptions: {
          style: (e) => {
            if (this.zoneToBeHighlighted && e.properties.name === this.zoneToBeHighlighted) {
              return {
                fillColor: 'green',
                weight: 2,
                opacity: 1,
                color: 'white',
                dashArray: '3',
                fillOpacity: 0.5
              }
            }
            return {
              fillColor: '#FC4E2A',
              weight: 2,
              opacity: 1,
              color: 'white',
              dashArray: '3',
              fillOpacity: this.zoneToBeHighlighted ? 0.5 : 0.7
            }
          },
          onEachFeature: (feature, layer) => {
            this.$nextTick(() => {
              this.addLabel(feature, layer)
            })
            layer.on({
              mouseover: this.highlightFeature,
              mouseout: this.resetHighlight,
              click: this.clickFeature
            })
            this.bindTooltipData(feature, layer)
          }
        },
        dynamicZoom: 7,
        dynamicCenter: [45.6443425111139, -74.65400692695313],
        tileProviders: [
          {
            name: 'Satellite',
            visible: false,
            url:
              'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
          },
          {
            name: 'OpenStreetMap',
            visible: false,
            url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
          },
          {
            name: 'OpenStreetMapNoLabels',
            visible: true,
            url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}{r}.png'
          }
        ],
  
        value: {
          metric: '',
          key: 'items'
        },
  
        geojson: this.coordinates,
        colorScale: ['e7d090', 'e9ae7b', 'de7062'],
        currentStrokeColor: '3d3213',
        errorMessage: ''
      }
    },
    props: {
    coordinates: {
      type: Object,
      required: true
    },
    zoneParamToShow: {
      type: Object,
      required: true
    },
    zoneToBeHighlighted: {
      required: false,
      type: String,
      default: undefined
    }
  },
    components: {
      LMap,
      LTileLayer,
      LControlScale,
      LControlZoom,
      LControlLayers,
      LGeoJson
    },
    mounted () {
      this.fetchGeoJsonData()
      this.updateOrInitializeLabels()
    },
    watch: {
      zoneParamToShow: {
        handler () {
          this.$nextTick(() => {
            this.updateOrInitializeLabels()
          })
        },
        deep: true
      },
      coordinates: {
        handler () {
          this.$nextTick(() => {
            this.updateOrInitializeLabels()
          })
        },
        deep: true
      }
    },
    created () {
    },
    methods: {
      bindTooltipData (feature, layer) {
        if (feature.properties && feature.properties.name) {
          const param = this.zoneParamToShow && this.zoneParamToShow.label || ''
          const paramValue = param && feature.properties.params[this.$t('Dashboard.' + param)]
          const paramtoShow = param !== '' ? `${this.$t('Dashboard.' + param)}: ${paramValue}` : ''
              const tooltipContent = `
              ${DOMPurify.sanitize(feature.properties.name)}<br>
              ${DOMPurify.sanitize(`<p>${paramtoShow}</p>`)}
              `
              layer.bindTooltip(tooltipContent)
            }
      },
      styleBorders () {
      return {
        color: '#aaa',
        weight: 1,
        fillOpacity: 0
      }
    },
    async fetchGeoJsonData () {
      try {
        const usResponse = await fetch('https://raw.githubusercontent.com/PublicaMundi/MappingAPI/master/data/geojson/us-states.json')
        this.usStatesData = await usResponse.json()
        const canadaResponse = await fetch('https://raw.githubusercontent.com/codeforamerica/click_that_hood/master/public/data/canada.geojson')
        await canadaResponse.json().then((data) => {
          this.canadaProvincesData = data
          this.$nextTick(() => {
            this.isLoading = false
            if (this.zoneToBeHighlighted) {
              this.$nextTick(() => {
                this.autoZoomAndCenter()
              })
            }
          })
        })
      } catch (error) {
        console.error('Error fetching GeoJSON data:', error)
        this.errorMessage = error.message
      }
    },
      highlightFeature (e) {
        var layer = e.target
        layer.setStyle({
          fillColor: 'green',
          weight: 5,
          color: '#666',
          dashArray: '',
          fillOpacity: 0.7
        })
  
        if (!L.Browser.ie && !L.Browser.opera && !L.Browser.edge) {
          layer.bringToFront()
        }
      },
      resetHighlight (e) {
        var layer = e.target
        layer.setStyle({
          fillColor: '#FC4E2A',
          weight: 2,
          opacity: 1,
          color: 'white',
          dashArray: '3',
          fillOpacity: 0.7
        })
  
        if (!L.Browser.ie && !L.Browser.opera && !L.Browser.edge) {
          layer.bringToFront()
        }
      },
      clickFeature (ev) {
        const feature = ev.target && ev.target.feature
        const featureProps = feature && feature.properties
        this.$emit('highlightTableRow', featureProps.name)
      },
      truncateText (text, maxLength) {
        return text.length > maxLength ? text.substr(0, maxLength - 1) + '…' : text
      },
      addLabel (feature, layer) {
        if (feature.properties && feature.properties.name) {
          const param = this.zoneParamToShow && this.zoneParamToShow.label || ''
          let paramValue = ''
          let paramtoShow = ''
          if (param && feature.properties.params) {
            paramValue = feature.properties.params[this.$t('Dashboard.' + param)]
            paramtoShow = `${this.$t('Dashboard.' + param)}: ${paramValue}`
          }
          if (paramValue === undefined) {
            console.warn(`Parameter ${param} not found for feature ${feature.properties.name}`)
            return null
          }
          const center = layer.getBounds().getCenter()
          const label = L.divIcon({
            className: 'label-icon',
            html: `<div><p>${this.truncateText(feature.properties.name, 15)}</p><p>${this.truncateText(paramtoShow, 20)}</p></div>`,
            iconSize: [120, 40]
          })
          if (!this.labelLayers) {
            this.labelLayers = {}
          }
          let marker = this.labelLayers[feature.properties.name]
          if (marker) {
            // Update existing marker
            marker.setLatLng(center)
            marker.setIcon(label)
          } else {
            // Create new marker
            marker = L.marker(center, { icon: label, interactive: false })
            if (this.$refs.heatMap && this.$refs.heatMap.mapObject) {
              marker.addTo(this.$refs.heatMap.mapObject)
              this.labelLayers[feature.properties.name] = marker
            }
          }
          return marker
        }
        return null
      },
      updateOrInitializeLabels () {
        if (this.$refs.geolayer && this.$refs.geolayer.mapObject) {
          const currentFeatures = new Set()

          this.$refs.geolayer.mapObject.eachLayer((layer) => {
            if (layer.feature && layer.feature.properties && layer.feature.properties.name) {
              currentFeatures.add(layer.feature.properties.name)
              this.bindTooltipData(layer.feature, layer)
              this.addLabel(layer.feature, layer)
            }
          })

          if (this.labelLayers) {
            Object.keys(this.labelLayers).forEach(featureName => {
              if (!currentFeatures.has(featureName)) {
                this.labelLayers[featureName].remove()
                delete this.labelLayers[featureName]
              }
            })
          }
        }
      },
      autoZoomAndCenter () {
          if (this.$refs.geolayer && this.$refs.geolayer.mapObject) {
            const features = this.coordinates.features
            const bounds = bbox(featureCollection(features))
            this.$refs.heatMap.fitBounds([[bounds[1], bounds[0]], [bounds[3], bounds[2]]])
          }
      }
    }
  }
  </script>
  
<style scoped>
@import "../../../../../node_modules/leaflet/dist/leaflet.css";

.map {
  height: 600px;
  width: 100%;
}

.label-icon {
  background: none;
  border: none;
  font-size: 10px;
  margin-top: 15rem;
  color: white;
}

.label-icon div {
  text-align: center;
  color: white;
  text-shadow: 1px 1px 2px black;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.error-text {
  color: #ff0000;
}
</style>
